/**
 * @name UploadFile
 * @description Componente común para añadir archivos adjuntos (Drag and Drop)
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';

// Material-UI Dropzone
import { DropzoneAreaBase } from 'material-ui-dropzone';

import { SnackbarService } from '@services';
import { Flex, Icons, MUI, Unicons, Theme, Typography } from '@styles';

// *************************************
// ACCEPTED FORMATS                               
// *************************************

// ZIP = application/zip
// ZIP_COMPRESSED = application/x-zip-compressed
// ZIP_STREAM = application/octet-stream
// PDF = application/pdf
// DOCX = application/msword
// XLSX = application/vnd.ms-excel
// DOCX_OFFICE = application/vnd.openxmlformats-officedocument.wordprocessingml.document
// XLSX_OFFICE = application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
// PPTX_OFFICE = application/vnd.openxmlformats-officedocument.presentationml.presentation
// PNG = image/png
// JPG = image/jpg
// JPEG = image/jpeg
// CSV = text/csv
// JSON = text/json
// JSON =  application/json

class UploadFile extends Component {
  constructor(props) {
    super(props);
    // Se obtiene el tamaño máximo configurado por el administrador si esta logueado con token si no obtiene uno por default (3 MB) o el asigando por el component UploadFile.
    this.enterpriseParams = JSON.parse(localStorage.getItem('ls.infoUsuario')) ? JSON.parse(localStorage.getItem('ls.infoUsuario')).Empresa : null;
    this.maxSizeEnterprise = this.enterpriseParams ? this.enterpriseParams['TamañoArchivo'] : 3;
    this.maxSizeMessage = props.maxSize ? props.maxSize : this.maxSizeEnterprise;

    this.state = {
      files: [],
      typeIcon: '',
    };
  }

  componentDidMount() {
    const { fileObject } = this.props;
    this.setState({ files: fileObject });
  }

  handleFileAdd = (file) => {
    const { type } = file[0].file;
    let typeIcon;

    if (type.startsWith('video/')) {
      typeIcon = <Icons.Theaters />;
    }

    if (type.startsWith('audio/')) {
      typeIcon = <Icons.AudioTrack />;
    }

    switch (type) {
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        typeIcon = <Icons.FileWord />;
        break;
      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        typeIcon = <Icons.FileExcel />;
        break;
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        typeIcon = <Icons.FilePowerpoint />;
        break;
      case 'application/pdf':
        typeIcon = <Icons.FilePDF />;
        break;
      default:
        typeIcon = <Unicons.PaperClip size="20" />;
        break;
    }

    this.setState({ files: file, typeIcon });
    return this.props.onChangeFile(file);
  };

  handleFileDelete = () => {
    this.setState({ files: [] });
    return this.props.onChangeFile([]);
  };

  render() {
    const { files, typeIcon } = this.state;

    const materialTheme = createTheme({
      overrides: {
        MuiDropzoneArea: {
          root: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '150px',
            borderColor: Theme.colors.greyMedium,
            '&:hover': {
              borderColor: files.length >= 1 ? Theme.colors.greyMedium : Theme.colors.greyLight,
            }
          },
          icon: {
            display: 'none'
          },
          text: {
            padding: '8px',
            marginTop: '0px',
            marginBottom: '0px',
            fontFamily: 'Bryant Pro Regular',
            fontSize: Theme.typography.bodyText,
            fontWeight: 'normal',
            letterSpacing: '0.0175em',
            whiteSpace: 'normal',
          }
        },
        MuiDropzonePreviewList: {
          root: {
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px'
          },
          imageContainer: {
            padding: '0 8px !important',
            margin: '8px',
            '& .MuiChip-root': {
              borderColor: Theme.colors.greyDark,
              color: Theme.colors.greyDark,
              height: '100%',
              minHeight: '36px'
            },
            '& .MuiChip-label': {
              whiteSpace: 'break-spaces',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            '& .MuiChip-icon': {
              color: Theme.colors.greyDark,
            },
            '& .MuiChip-outlined .MuiChip-icon': {
              marginLeft: '8px'
            },
            '& .MuiChip-outlined .MuiChip-deleteIcon': {
              marginRight: '8px'
            },
            '& svg': {
              fontSize: '20px',
              height: '20px',
              width: '20px'
            }
          }
        }
      }
    });

    const dropzoneTextComponent = (
      <>
        <Flex.Col alignItems="center" justifyContent="center" padding="8px">
          <Typography.Small>{i18n.t('UploadFile.dragAndDrop')}</Typography.Small>
          <Styled.ButtonLink>{i18n.t('UploadFile.browseFile')}</Styled.ButtonLink>
          <Flex.Col padding="4px 0 0">
            {/* Formatos Archivos */}
            <Typography.Small>
              <Typography.Medium>{`${i18n.t('UploadFile.files')}: `}</Typography.Medium>
              {this.props.formatsMessage}
            </Typography.Small>
            {/* Tamaño Max */}
            <Typography.Text>
              <Typography.Medium>{`(${i18n.t('GenericMessages.max')} ${this.maxSizeMessage} MB)`}</Typography.Medium>
            </Typography.Text>
          </Flex.Col>
        </Flex.Col>
        {/* Icono */}
        {files.length === 0 && (
          <Flex.Row justifyContent="center" padding="8px">
            <Unicons.Upload size="28" />
          </Flex.Row>
        )}
      </>
    );

    return (
      <ThemeProvider theme={materialTheme}>
        <DropzoneAreaBase
          acceptedFiles={this.props.formatsAccepted}
          dropzoneText={dropzoneTextComponent}
          dropzoneProps={{ disabled: Boolean(files.length >= 1), maxFiles: 1, multiple: false }}
          filesLimit={1}
          fileObjects={files}
          maxFileSize={this.props.maxSize * 1048576}
          showAlerts={false}
          useChipsForPreview={true}
          previewChipProps={{ icon: typeIcon }}
          getDropRejectMessage={(rejectedFile, acceptedFiles, maxFileSize) => {
            if (acceptedFiles.length > 0 && !acceptedFiles.find((format) => format === rejectedFile.type)) { SnackbarService.error(`${i18n.t('UploadFile.messageFileRejected', { fileName: rejectedFile.name })}${i18n.t('UploadFile.messageInvalidType')}`); }
            if (rejectedFile.size > maxFileSize) { SnackbarService.error(`${i18n.t('UploadFile.messageFileRejected', { fileName: rejectedFile.name })}${i18n.t('UploadFile.messageFileTooBig', { maxSize: (maxFileSize / 1048576) })}`); }
          }}
          getFileAddedMessage={(fileName) => SnackbarService.info(i18n.t('UploadFile.messageAddedFile', { fileName }))}
          getFileRemovedMessage={(fileName) => SnackbarService.info(i18n.t('UploadFile.messageRemovedFile', { fileName }))}
          onAdd={(file) => this.handleFileAdd(file)}
          onDelete={(file) => this.handleFileDelete(file)}
        />
      </ThemeProvider>
    );
  }
}

UploadFile.propTypes = {
  // Objeto de tipo arreglo donde se guarda el archivo adjunto.
  fileObject: PropTypes.array,
  // Arreglo de formatos admitidos.
  formatsAccepted: PropTypes.array,
  // Mensaje de formatos admitidos.
  formatsMessage: PropTypes.string,
  // Tamaño máximo permitido del archivo adjunto.
  maxSize: PropTypes.number,
  // Función a ejecutar cuando se añade/elimina el archivo ajunto.
  onChangeFile: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.ButtonLink = styled(MUI.ButtonLink)`
  &.MuiButton-root {
    min-height: auto;
  }

  .MuiButton-label {
    justify-content: flex-start;
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
  }
`;

export default UploadFile;
